import dayjs from 'dayjs';
import { LogOutIcon, ShieldCheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { appConfig, type FederationKey } from 'shared';
import { CountryFlag } from '~/modules/common/country-flag';
import { ExpandableTileDetail, type TileTone } from '~/modules/common/expandable-tile';
import { DeviceTile } from '~/modules/me/device-tile';
import type { Session } from '~/modules/me/types';
import { Badge } from '~/modules/ui/badge';
import { Button } from '~/modules/ui/button';
import { dateShort } from '~/utils/date-short';
import { regionName } from '~/utils/region-name';

/** The federation keys the app declares. An app without federations types the record by `string`, which names none. */
type DeclaredFederation = string extends FederationKey ? never : FederationKey;

/** A session that still authenticates: not revoked and not past its expiry. */
export const isLiveSession = (session: Session) => session.revokedAt === null && new Date(session.expiresAt).getTime() > Date.now();

/** When the session stopped authenticating, or will: its revocation, else its expiry. */
export const sessionEndedAt = (session: Session) => session.revokedAt ?? session.expiresAt;

/** Per status: its color and the label of the date it ends. */
const statuses = {
  current: { tone: 'success', endLabel: 'c:expires_at' },
  active: { tone: 'info', endLabel: 'c:expires_at' },
  expired: { tone: 'muted', endLabel: 'c:expired' },
  signed_out: { tone: 'muted', endLabel: 'c:signed_out' },
} as const satisfies Record<string, { tone: TileTone; endLabel: string }>;

const sessionStatus = (session: Session): keyof typeof statuses => {
  if (session.revokedAt !== null) return 'signed_out';
  if (!isLiveSession(session)) return 'expired';
  return session.isCurrent ? 'current' : 'active';
};

interface SessionTileProps {
  session: Session;
  /** Session from the same device as the current session (matched on deviceIdHash). */
  isCurrentDevice?: boolean;
  /** Absent in the history list: a revoked or expired session has nothing left to revoke. */
  onRevoke?: (sessionIds: string[]) => void;
  /** This session's revoke is running. */
  loading?: boolean;
}

export function SessionTile({ session, isCurrentDevice, onRevoke, loading }: SessionTileProps) {
  const { t, i18n } = useTranslation();
  /** A federation names itself in config; every other method has a locale key. */
  const strategyLabel = (strategy: Session['authStrategy']) =>
    strategy in appConfig.federations
      ? appConfig.federations[strategy as FederationKey].label
      : t(`c:${strategy as Exclude<typeof strategy, DeclaredFederation>}`);

  const status = sessionStatus(session);
  const isLive = status === 'current' || status === 'active';
  const countryName = session.ipCountry ? regionName(session.ipCountry, i18n.language) : null;
  const when = isLive ? t('c:signed_in_relative', { time: dayjs(session.createdAt).fromNow() }).toLowerCase() : dateShort(sessionEndedAt(session));

  return (
    <DeviceTile
      value={session.id}
      device={session}
      status={{ tone: statuses[status].tone, label: t(`c:${status}`) }}
      badges={
        <>
          {session.type === 'mfa' && (
            <span className="text-success flex shrink-0 items-center gap-1">
              <ShieldCheckIcon className="size-4" />
              <span className="text-xs font-medium max-sm:sr-only">{t('c:mfa_short')}</span>
            </span>
          )}
          {isCurrentDevice && <Badge variant="outline">{t('c:this_device')}</Badge>}
          {session.isNewDevice && !session.isCurrent && !isCurrentDevice && <Badge variant="warning">{t('c:new_device')}</Badge>}
        </>
      }
      meta={
        <>
          {/* object-cover, so the radius rounds the flag and not the space around it */}
          {session.ipCountry && <CountryFlag countryCode={session.ipCountry} height={11} className="mr-1.5 rounded-xs object-cover align-[-1px]" />}
          {when}
        </>
      }
      details={
        <>
          <ExpandableTileDetail label={t('c:signed_in')}>{dateShort(session.createdAt)}</ExpandableTileDetail>
          <ExpandableTileDetail label={t(statuses[status].endLabel)}>{dateShort(sessionEndedAt(session))}</ExpandableTileDetail>
          {/* A plain sign-out is the status itself */}
          {session.revocationReason && session.revocationReason !== 'sign_out' && (
            <ExpandableTileDetail label={t('c:reason')}>{t(`c:revocation_reason.${session.revocationReason}`)}</ExpandableTileDetail>
          )}
          <ExpandableTileDetail label={t('c:strategy')}>{strategyLabel(session.authStrategy)}</ExpandableTileDetail>
          {countryName && <ExpandableTileDetail label={t('c:country')}>{countryName}</ExpandableTileDetail>}
        </>
      }
      actions={
        status === 'active' &&
        onRevoke && (
          <Button variant="secondary" size="sm" className="max-sm:w-full" loading={loading} onClick={() => onRevoke([session.id])}>
            <LogOutIcon />
            {t('c:sign_out')}
          </Button>
        )
      }
    />
  );
}
